/*
  Mobile pinch-to-zoom for product gallery (non-modal)
  - Applies to images inside `.gallery-top .product__media.media`
  - Prevents browser/page zoom via touch-action and pointer handling
  - Disables Swiper slide swiping while zoomed
*/
;(function () {
  const ready = (f) =>
    document.readyState === "loading"
      ? document.addEventListener("DOMContentLoaded", f, { once: true })
      : f()

  ready(() => {
    // Only enable on small screens
    const isMobile = () => window.innerWidth <= 1023

    // Support both custom Swiper gallery and Dawn default media list
    const STAGE_SELECTOR =
      ".gallery-top .swiper-slide .product__media.media, .product__media-list .product__media.media"
    const IMG_SELECTOR = "img"
    const MIN_SCALE = 1
    const MAX_SCALE = 2.5
    const DEFAULT_ZOOM = 1.8 // not used for pinch start; reserved for double-tap use if needed

    // Keep a reference to swiper instance if available
    const getSwiper = () =>
      document.querySelector(".gallery-top .swiper-container")?.swiper || null

    class PinchZoom {
      constructor(stage) {
        this.stage = stage
        this.img = stage.querySelector(IMG_SELECTOR)
        if (!this.img) return

        this.isZoomed = false
        this.scale = 1
        this.pos = { x: 0, y: 0 }
        this.tScale = 1
        this.tPos = { x: 0, y: 0 }
        this.animating = false

        this.activeTouches = new Map()
        this.touchDragging = false
        this.lastDrag = null
        this.pinchStartDist = 0
        this.pinchStartScale = 1

        // Style tweaks
        this.img.style.willChange = "transform"
        this.img.style.transformOrigin = "center center"
        // Prevent native gestures inside the stage
        this.stage.style.touchAction = "none"
        this.stage.style.webkitUserSelect = "none"
        this.stage.style.userSelect = "none"

        this.addEvents()
      }

      rectOf(el) {
        return el.getBoundingClientRect()
      }

      boundsFor(scale) {
        const r = this.rectOf(this.stage)
        return {
          maxX: (r.width * (scale - 1)) / 2,
          maxY: (r.height * (scale - 1)) / 2,
        }
      }

      clamp(v, a, b) {
        return Math.max(a, Math.min(b, v))
      }

      apply() {
        this.img.style.transform = `translate3d(${this.pos.x}px, ${this.pos.y}px, 0) scale(${this.scale})`
      }

      startAnim() {
        if (this.animating) return
        this.animating = true
        const step = () => {
          const lerp = (a, b, t) => a + (b - a) * t
          this.scale = lerp(this.scale, this.tScale, 0.25)
          this.pos = {
            x: lerp(this.pos.x, this.tPos.x, 0.25),
            y: lerp(this.pos.y, this.tPos.y, 0.25),
          }
          this.apply()
          if (
            Math.abs(this.scale - this.tScale) > 0.002 ||
            Math.abs(this.pos.x - this.tPos.x) > 0.5 ||
            Math.abs(this.pos.y - this.tPos.y) > 0.5
          ) {
            requestAnimationFrame(step)
          } else {
            this.scale = this.tScale
            this.pos = { ...this.tPos }
            this.apply()
            this.animating = false
          }
        }
        requestAnimationFrame(step)
      }

      enableZoomMode() {
        if (this.isZoomed) return
        this.isZoomed = true
        // do not jump scale on pinch-start; let pinch drive the scale
        this.toggleSwiper(false)
      }

      disableZoomMode() {
        this.isZoomed = false
        this.tScale = this.scale = 1
        this.tPos = this.pos = { x: 0, y: 0 }
        this.apply()
        this.toggleSwiper(true)
      }

      toggleSwiper(allow) {
        try {
          const sw = getSwiper()
          if (sw) sw.allowTouchMove = !!allow
        } catch (e) {}
      }

      resetOnSlideChange() {
        this.disableZoomMode()
      }

      // Helpers for touches
      distance(a, b) {
        const dx = a.x - b.x,
          dy = a.y - b.y
        return Math.hypot(dx, dy)
      }
      midpoint(a, b) {
        return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
      }

      addEvents() {
        // iOS Safari: prevent native pinch zoom within stage
        this.stage.addEventListener(
          "gesturestart",
          (e) => {
            e.preventDefault()
          },
          { passive: false },
        )
        this.stage.addEventListener(
          "gesturechange",
          (e) => {
            e.preventDefault()
          },
          { passive: false },
        )
        this.stage.addEventListener(
          "gestureend",
          (e) => {
            e.preventDefault()
          },
          { passive: false },
        )

        // Single finger drag when zoomed
        this.stage.addEventListener("pointerdown", (e) => {
          if (!isMobile()) return
          if (e.pointerType !== "touch") return
          this.activeTouches.set(e.pointerId, { x: e.clientX, y: e.clientY })
          this.stage.setPointerCapture?.(e.pointerId)
          if (this.isZoomed && this.activeTouches.size === 1) {
            this.touchDragging = true
            this.lastDrag = { x: e.clientX, y: e.clientY }
          }
          // Begin pinch
          if (!this.isZoomed && this.activeTouches.size === 2) {
            const [a, b] = [...this.activeTouches.values()]
            this.pinchStartDist = this.distance(a, b)
            this.pinchStartScale = this.scale
            this.enableZoomMode()
          }
        })

        this.stage.addEventListener(
          "pointermove",
          (e) => {
            if (!isMobile()) return
            if (
              e.pointerType !== "touch" ||
              !this.activeTouches.has(e.pointerId)
            )
              return
            this.activeTouches.set(e.pointerId, { x: e.clientX, y: e.clientY })

            if (this.activeTouches.size >= 2) {
              const [a, b] = [...this.activeTouches.values()]
              if (this.pinchStartDist === 0) {
                this.pinchStartDist = this.distance(a, b)
                this.pinchStartScale = this.scale
              }
              const dist = this.distance(a, b)
              const center = this.midpoint(a, b)
              const newScale = this.clamp(
                this.pinchStartScale * (dist / this.pinchStartDist),
                MIN_SCALE,
                MAX_SCALE,
              )
              this.tScale = newScale
              // re-anchor to pinch center
              const r = this.rectOf(this.stage)
              const nx = (center.x - (r.left + r.width / 2)) / (r.width / 2)
              const ny = (center.y - (r.top + r.height / 2)) / (r.height / 2)
              const { maxX, maxY } = this.boundsFor(this.tScale)
              this.tPos = {
                x: this.clamp(-nx * maxX, -maxX, maxX),
                y: this.clamp(-ny * maxY, -maxY, maxY),
              }
              this.startAnim()
              return
            }
            // Drag
            if (this.isZoomed && this.touchDragging && this.lastDrag) {
              const dx = e.clientX - this.lastDrag.x
              const dy = e.clientY - this.lastDrag.y
              const { maxX, maxY } = this.boundsFor(this.tScale)
              this.tPos = {
                x: this.clamp(this.tPos.x + dx, -maxX, maxX),
                y: this.clamp(this.tPos.y + dy, -maxY, maxY),
              }
              this.lastDrag = { x: e.clientX, y: e.clientY }
              this.startAnim()
            }
          },
          { passive: true },
        )

        const endTouchFor = (id) => {
          if (this.activeTouches.has(id)) this.activeTouches.delete(id)
          if (this.activeTouches.size < 2) {
            this.pinchStartDist = 0
            this.pinchStartScale = this.tScale
          }
          if (this.activeTouches.size === 0) {
            this.touchDragging = false
            this.lastDrag = null
            if (this.tScale <= 1.01) this.disableZoomMode()
          }
        }

        this.stage.addEventListener("pointerup", (e) => {
          if (e.pointerType === "touch") endTouchFor(e.pointerId)
          this.stage.releasePointerCapture?.(e.pointerId)
        })
        this.stage.addEventListener("pointercancel", (e) => {
          if (e.pointerType === "touch") endTouchFor(e.pointerId)
        })

        // Prevent accidental image dragging
        this.stage.addEventListener("dragstart", (e) => e.preventDefault())

        // Extra safety: block two-finger touchmove default if any
        this.stage.addEventListener(
          "touchmove",
          (e) => {
            if (e.touches && e.touches.length >= 2) e.preventDefault()
          },
          { passive: false },
        )
      }
    }

    const instances = []

    const init = () => {
      // Attach to all current image stages
      document.querySelectorAll(STAGE_SELECTOR).forEach((stage) => {
        if (stage.__pinchZoomInstance) return
        const img = stage.querySelector(IMG_SELECTOR)
        if (!img) return
        const inst = new PinchZoom(stage)
        stage.__pinchZoomInstance = inst
        instances.push(inst)
      })

      // Reset zoom when slide changes
      try {
        const sw = getSwiper()
        if (sw && !sw.__pinchHooked) {
          sw.on("slideChange", () =>
            instances.forEach((i) => i.resetOnSlideChange()),
          )
          sw.__pinchHooked = true
        }
      } catch (e) {}
    }

    // Initialize now and on dynamic updates
    init()
    // In case Swiper re-renders, observe the gallery area
    const gallery = document.querySelector(".gallery-top")
    if (gallery) {
      new MutationObserver(() => init()).observe(gallery, {
        childList: true,
        subtree: true,
      })
    }

    window.addEventListener("resize", () => {
      if (!isMobile()) instances.forEach((i) => i.disableZoomMode())
    })
  })
})()
